import {
  ApiError,
  createMatch,
  fetchScenarios,
  recentOpponents,
  suggestedOpponents,
  type Scenario,
  type SeatPlan,
  type Session,
} from "../api";
import { showAvatar } from "../avatars";
import { footer } from "../chrome";
import { el, render } from "../dom";
import { withHandleTypeahead } from "../handle-typeahead";
import { nav } from "../nav";
// A type-only import stays a type: the module itself — thirty inlined
// sprites — is fetched dynamically below, off the eager bundle the
// analytics test keeps a ceiling on.
import type { ScenarioPreview } from "../scenario/preview";
import {
  camoModal,
  camoPicker,
  pickedBlob,
  pickedPixels,
  type Picker,
} from "./camo-picker";
import { scenarioPool, type ChallengeMode } from "./scenario-pool";
import { waitingScreen } from "./waiting";

export type { ChallengeMode } from "./scenario-pool";

/** What a seat select's value means: the player, a bot, or `p:<handle>`. */
type SeatChoice = "self" | "bot" | `p:${string}`;

/** "@handle", for chips and options; handles are stored bare. */
function at(handle: string): string {
  return `@${handle}`;
}

/**
 * A pilot tile: an avatar, a handle, and a rank row. Named pilotTile rather
 * than pilotCard so a grep for either name does not also turn up the
 * unrelated stats card pilot.ts builds for Home.
 *
 * `rank` is stubbed at "n/a" everywhere it is called today — there is no
 * rank, kill count, or any other per-player stat anywhere in this codebase.
 * The parameter exists so a real value slots in later without redesigning
 * every call site; the row always renders rather than being hidden or left
 * out, so nothing has to be un-hidden when that day comes.
 *
 * `actor` is who showAvatar() looks up (a DID or a handle, whichever the
 * caller has); `handle` is what gets printed, and may be null for an
 * account the caller only knows by DID.
 */
function pilotTile(
  actor: string,
  handle: string | null,
  rank = "n/a",
): HTMLElement {
  const face = el("img", { className: "pilot-tile-avatar", alt: "" });
  showAvatar(face, actor);
  return el("span", { className: "pilot-tile-face" }, [
    face,
    el("span", { className: "pilot-tile-info" }, [
      el("span", {
        className: "pilot-tile-handle",
        textContent: handle ? at(handle) : `${actor.slice(0, 16)}…`,
      }),
      el("span", {
        className: "pilot-tile-rank",
        textContent: `Rank: ${rank}`,
      }),
    ]),
  ]);
}

/**
 * A small error badge. Empty text hides it; `role="status"` reads a change
 * out loud without stealing focus from the control that caused it.
 */
function errorPill(): { node: HTMLElement; say(text: string): void } {
  const node = el("span", { className: "pill pill-error", role: "status" });
  node.hidden = true;
  return {
    node,
    say(text: string): void {
      node.textContent = text;
      node.hidden = text === "";
    },
  };
}

/**
 * How many other players can be lined up: one, for now. The API takes any
 * number, but arena serves a single browser client per match, so a second
 * lined-up player would be a seat nobody can sit in. The greyed button this
 * cap pairs with is where more players go when that lands.
 */
const MAX_LINEUP = 1;

/** How many recent opponents the strip beside the typeahead lists. */
const RECENT_SHOWN = 5;

/** How many suggested opponents (followed, and have played before) show. */
const SUGGESTED_SHOWN = 5;

/**
 * Pick a scenario, line up opponents, and map everyone to its slots.
 *
 * The lineup is the middle of the flow: the recent-opponents strip and the
 * typeahead both feed it, and the slot selects read from it. Princess is not
 * in it — she is the standing answer for any slot nobody claims.
 */
export function challengeScreen(session: Session, mode: ChallengeMode): Node[] {
  const status = el("p", { className: "error", role: "alert" });
  const next = el("button", { type: "button", textContent: "Deploy" });

  /** Handles lined up to fight, in the order they were added. */
  const lineup: string[] = [];
  /** The chosen scenario, null until the catalog arrives. */
  let scenario: Scenario | null = null;
  /** slot name -> who sits in it. */
  const seats = new Map<string, SeatChoice>();

  /**
   * One picker per AI seat, kept by slot name so a scenario change or a
   * seat rebuild does not throw away what the seat is wearing.
   *
   * A bot's camo used to be a roll this screen made for it and the player
   * could only reroll. It is a choice now, made the same way their own is:
   * the same strip, the same dialog, the same swatch - opening on a roll,
   * because a force with no account has no default to open on.
   */
  const botCamo = new Map<
    string,
    { picker: Picker; button: HTMLButtonElement; dialog: HTMLDialogElement }
  >();

  function botCamoFor(slot: string) {
    let held = botCamo.get(slot);
    if (!held) {
      const picker = camoPicker(session, "random", false);
      picker.onChange = refreshPreview;
      const opened = camoModal(picker, `Choose ${slot}'s camo`, "swatch");
      held = { picker, ...opened };
      botCamo.set(slot, held);
    }
    return held;
  }

  /**
   * The preview, mounted when its chunk lands. Lazily and never fatally,
   * like the camo strip: the sprites are the heaviest thing this screen
   * asks for, and a match must still be startable without pictures.
   */
  const previewMount = el("div", { className: "scenario-preview-mount" });
  let preview: ScenarioPreview | null = null;
  void import("../scenario/preview")
    .then((mod) => {
      preview = mod.scenarioPreview();
      previewMount.append(preview.node);
      refreshPreview();
    })
    .catch((error: unknown) => {
      console.warn("challenge: the scenario preview could not load", error);
    });

  /** Who holds a slot, the way the preview captions it. */
  function describeSlot(slot: string): string {
    const choice = seats.get(slot) ?? "bot";
    if (choice === "self") return "You";
    if (choice === "bot") return "AI";
    return at(choice.slice(2));
  }

  /** The camo a force previews in: yours and the AI's as each is picked, an
   * invited player's not at all — theirs is theirs to bring.
   *
   * Through the picker's own reader rather than its record key: a roll is
   * bytes and has no key, so reading the key previewed it as bare grey. */
  async function camoForSlot(slot: string): Promise<ImageData | null> {
    const choice = seats.get(slot) ?? "bot";
    if (choice === "bot") return pickedPixels(botCamoFor(slot).picker);
    if (choice !== "self") return null;
    return pickedPixels(camo);
  }

  function refreshPreview(): void {
    preview?.update(scenario, describeSlot, camoForSlot);
  }

  const camo = camoPicker(session);
  // Repainting on every selection keeps the preview wearing the camo the
  // launch will actually send.
  camo.onChange = refreshPreview;
  // The same swatch and dialog the hangar and the lobby open the picker with.
  // It lives in the seat row the player is taking - see `setCamo` - rather
  // than in a block of its own at the bottom of the card, which named no
  // force at all.
  const camoOpen = camoModal(camo, "Choose camo", "swatch");

  // ----- opponents ---------------------------------------------------------

  const lineupStrip = el("div", { className: "lineup" });
  const recentStrip = el("div", { className: "lineup recent-list" });
  const recentNote = el("p", {
    className: "hint",
    textContent: "Reading who you have fought…",
  });

  function renderLineup(): void {
    lineupStrip.replaceChildren();
    for (const handle of lineup) {
      const remove = el("button", {
        type: "button",
        className: "pilot-tile",
      });
      remove.append(
        pilotTile(handle, handle),
        el("span", { className: "pilot-tile-remove", textContent: "✕" }),
      );
      remove.setAttribute("aria-label", `Remove ${at(handle)}`);
      remove.addEventListener("click", () => {
        lineup.splice(lineup.indexOf(handle), 1);
        // Their seats go back to the AI rather than dangling.
        for (const [slot, choice] of seats) {
          if (choice === `p:${handle}`) seats.set(slot, "bot");
        }
        renderLineup();
        renderSlots();
      });
      lineupStrip.append(remove);
    }
    // A full lineup swaps the add form for the button that says where this
    // goes next. Swapped rather than disabled-in-place, so the typeahead is
    // not left looking like the way to a second opponent.
    addForm.hidden = lineup.length >= MAX_LINEUP;
    moreRow.hidden = lineup.length < MAX_LINEUP;
  }

  /**
   * Lines a player up, or answers why not — the sentence for the pill by the
   * control that asked.
   */
  function addToLineup(handle: string): string | null {
    const bare = handle.trim().replace(/^@/, "").toLowerCase();
    if (!bare) return null;
    if (bare === session.handle) return "You can't play yourself.";
    if (lineup.includes(bare)) return `${at(bare)} is already lined up.`;
    if (lineup.length >= MAX_LINEUP) return "One opponent for now.";
    lineup.push(bare);
    // Straight into the first seat the AI was holding, which is what
    // lining someone up means. A scenario with no seat open leaves them
    // listed; the selects offer them wherever the player wants them.
    if (scenario) {
      const open = scenario.slots.find(
        (slot) => (seats.get(slot.name) ?? "bot") === "bot",
      );
      if (open) seats.set(open.name, `p:${bare}`);
    }
    renderLineup();
    renderSlots();
    return null;
  }

  /**
   * What dropping a pilot tile onto a slot row means: line them up if they
   * are not already (the same refusals addToLineup would give from the
   * form or a click apply here too), then put them in *this* slot
   * specifically — addToLineup on its own assigns the first open slot,
   * which is not necessarily the one just dropped onto.
   */
  function placeInSlot(slotName: string, handle: string): void {
    const bare = handle.trim().replace(/^@/, "").toLowerCase();
    if (!bare) return;
    if (!lineup.includes(bare)) {
      const refused = addToLineup(bare);
      if (refused) {
        addPill.say(refused);
        return;
      }
    }
    seats.set(slotName, `p:${bare}`);
    renderSlots();
  }

  // The same element sign-in uses, wrapped in a form so Enter adds. The
  // typeahead swallows the Enter that picks a suggestion; the one after it —
  // value unchanged — falls through to the submit this form exists for.
  const addInput = el("input", {
    type: "text",
    name: "handle",
    placeholder: "their-handle.bsky.social",
    autocapitalize: "none",
    autocomplete: "off",
    spellcheck: false,
  });
  addInput.setAttribute("aria-label", "Another player's handle");
  const addButton = el("button", {
    type: "submit",
    className: "secondary small-btn",
    textContent: "Add",
  });
  const addForm = el("form", { className: "lineup-add" }, [
    withHandleTypeahead(addInput),
    addButton,
  ]);
  // Answers for both ways in — the form and the recent chips — so it sits
  // after the form, where either refusal happened.
  const addPill = errorPill();
  addForm.addEventListener("submit", (event) => {
    event.preventDefault();
    const refused = addToLineup(addInput.value);
    addPill.say(refused ?? "");
    if (refused) return;
    addInput.value = "";
    addInput.focus();
  });
  addInput.addEventListener("input", () => addPill.say(""));

  // Where a second opponent goes when there can be one. Greyed rather than
  // absent: the cap is temporary (arena seats one browser today), and a
  // control that exists says so better than a gap.
  const moreRow = el("div", { className: "lineup-more" }, [
    el("button", {
      type: "button",
      className: "secondary small-btn",
      textContent: "Add more players",
      disabled: true,
    }),
    el("span", { className: "hint", textContent: "Not yet available." }),
  ]);
  moreRow.hidden = true;

  // A card for one candidate opponent, wherever they came from — the recent
  // list and the suggested list read identically off just a did/handle, so
  // both build their strip from this rather than duplicating the markup.
  function opponentChip(did: string, handle: string | null): HTMLElement {
    // An account that lost its handle since (or was never one to begin
    // with, for a suggestion) is still shown, but there is no name to line
    // up, so its card stays inert.
    const chip = el("button", {
      type: "button",
      className: "pilot-tile",
      disabled: !handle,
    });
    chip.append(pilotTile(did, handle));
    if (handle) {
      chip.addEventListener("click", () =>
        addPill.say(addToLineup(handle) ?? ""),
      );
      // A pointer-only shortcut onto a slot row, on top of (never instead
      // of) the click-to-add above: native HTML5 drag has no accessible
      // keyboard equivalent, so click-to-add stays the way in for keyboard
      // and touch.
      chip.draggable = true;
      chip.addEventListener("dragstart", (event) => {
        event.dataTransfer?.setData("text/plain", handle);
        if (event.dataTransfer) event.dataTransfer.effectAllowed = "copy";
      });
    }
    return chip;
  }

  recentOpponents()
    .then((opponents) => {
      if (!opponents.length) {
        recentNote.textContent = "You have not fought anyone yet.";
        return;
      }
      recentNote.textContent = "Fought before — click to line them up again.";
      // The API sends up to a dozen, latest first; the column beside the
      // form only has room to say "recent", so only the freshest few show.
      for (const opponent of opponents.slice(0, RECENT_SHOWN)) {
        recentStrip.append(opponentChip(opponent.did, opponent.handle));
      }
    })
    .catch((error: unknown) => {
      console.warn("challenge: recent opponents could not be read", error);
      recentNote.textContent = "Who you have fought could not be read.";
    });

  const suggestedStrip = el("div", { className: "lineup recent-list" });
  const suggestedNote = el("p", {
    className: "hint",
    textContent: "Reading who you follow…",
  });

  // Issued as a sibling of, not nested inside, the recentOpponents() chain
  // above: a slow or failing AppView read must never delay the always-fast
  // local recent-opponents render.
  suggestedOpponents()
    .then((suggested) => {
      if (!suggested.length) {
        suggestedNote.textContent = "Nobody you follow has played yet.";
        return;
      }
      suggestedNote.textContent =
        "Following, and played before — click to line them up.";
      for (const opponent of suggested.slice(0, SUGGESTED_SHOWN)) {
        suggestedStrip.append(opponentChip(opponent.did, opponent.handle));
      }
    })
    .catch((error: unknown) => {
      console.warn("challenge: suggested opponents could not be read", error);
      suggestedNote.textContent = "Who you follow could not be read.";
    });

  const opponentsBlock = el("div", { className: "choice camo-pick" }, [
    el("strong", { textContent: "Opponents" }),
    // The typeahead is the way in; who you fought before and who you follow
    // sit beside it, as shortcuts into the same lineup.
    el("div", { className: "opponents-row" }, [
      el("div", { className: "opponents-add" }, [
        el("p", {
          className: "hint",
          textContent: "Line up another player by their handle.",
        }),
        addForm,
        addPill.node,
        moreRow,
        lineupStrip,
      ]),
      el("div", { className: "opponents-recent" }, [
        el("strong", {
          className: "opponents-subhead",
          textContent: "Recent",
        }),
        recentNote,
        recentStrip,
      ]),
      el("div", { className: "opponents-recent" }, [
        el("strong", {
          className: "opponents-subhead",
          textContent: "Suggested",
        }),
        suggestedNote,
        suggestedStrip,
      ]),
    ]),
  ]);

  // ----- scenario ----------------------------------------------------------

  const scenarioList = el("div", { className: "scenario-list" });
  const scenarioNote = el("p", {
    className: "hint",
    textContent: "Reading the scenario list…",
  });
  const scenarioBlock = el("div", { className: "choice camo-pick" }, [
    el("strong", { textContent: "Scenario" }),
    scenarioNote,
    // The list to choose from and, beside it, what the chosen one looks
    // like: each force's units on hexes, painted in the camo they will
    // actually fight in.
    el("div", { className: "scenario-body" }, [scenarioList, previewMount]),
  ]);

  function chooseScenario(chosen: Scenario): void {
    scenario = chosen;
    // A fresh mapping: the player first, the lineup in order, Princess for
    // the rest. Reassigned on every scenario change rather than carried
    // across, because the slots the old choices were made for are gone.
    seats.clear();
    const [first, ...rest] = chosen.slots;
    if (first) seats.set(first.name, "self");
    rest.forEach((slot, index) => {
      const handle = lineup[index];
      seats.set(slot.name, handle ? `p:${handle}` : "bot");
    });
    renderSlots();
  }

  fetchScenarios()
    .then((catalog) => {
      // Duel's one behavioral difference from Scenario: a narrower pool,
      // filtered client-side (see scenario-pool.ts) over the same catalog
      // the API already sends.
      const pool = scenarioPool(catalog, mode);
      scenarioNote.remove();
      pool.forEach((entry, index) => {
        const input = el("input", {
          type: "radio",
          name: "scenario",
          value: entry.path,
          checked: index === 0,
        });
        input.addEventListener("change", () => chooseScenario(entry));
        // No unit rundown here: the preview beside the list draws the
        // forces properly, and a line of designations next to it is noise.
        scenarioList.append(
          el("label", { className: "choice" }, [
            input,
            el("div", {}, [
              el("strong", { textContent: entry.name }),
              el("p", { className: "hint", textContent: entry.description }),
            ]),
          ]),
        );
      });
      if (pool[0]) chooseScenario(pool[0]);
    })
    .catch((error: unknown) => {
      console.warn("challenge: scenarios could not be read", error);
      scenarioNote.textContent =
        "The scenario list could not be read; go back and try again.";
      updateNext();
    });

  // ----- slots -------------------------------------------------------------

  const slotList = el("div", { className: "slot-list" });
  const seatNote = el("p", {
    className: "hint",
    textContent: "Any slot you leave alone is played by the AI.",
  });
  // Validation lives in a pill of its own rather than rewriting the hint, so
  // an error arriving and leaving does not make the explanatory line jump.
  const seatPill = errorPill();
  // Who "the AI" is, once, beside the list rather than repeated per slot.
  const aiCallout = el("aside", { className: "callout" }, [
    el("strong", { textContent: "Princess" }),
    el("p", {
      className: "hint",
      textContent:
        "AI slots are played by Princess, MegaMek's built-in bot. " +
        "On lance.blue she fights as @lance.blue.",
    }),
  ]);
  const slotsBlock = el("div", { className: "choice camo-pick" }, [
    el("strong", { textContent: "Slots" }),
    seatNote,
    seatPill.node,
    el("div", { className: "slot-body" }, [slotList, aiCallout]),
  ]);

  function renderSlots(): void {
    slotList.replaceChildren();
    if (!scenario) return;
    for (const slot of scenario.slots) {
      const select = el("select");
      select.setAttribute("aria-label", `Who fights as ${slot.name}`);
      // The controller's face, beside the control that decides it. Princess
      // has no account, so her face is the site's own mark - deterministic,
      // and served from here rather than looked up.
      const face = el("img", { className: "seat-avatar", alt: "" });
      const setFace = (choice: string): void => {
        if (choice === "bot") {
          face.dataset.actor = "";
          face.src = "/logo.svg";
          face.hidden = false;
        } else {
          showAvatar(face, choice === "self" ? session.did : choice.slice(2));
        }
      };
      setFace(seats.get(slot.name) ?? "bot");
      // The camo, on the seat it paints, whoever is holding it: the player's
      // own swatch on the seat they take, and that seat's bot swatch when the
      // AI has it. A seat handed to another player shows none - their camo is
      // theirs to bring, and this screen has no way to know it.
      //
      // The player's swatch is one control moved between rows rather than one
      // per row: there is one selection, and exactly one seat can be `self`.
      // A row rebuilt for a new scenario takes it back, which is why this
      // runs on build as well as on change.
      const setCamo = (choice: string): void => {
        for (const child of [...pick.children]) {
          if (child instanceof HTMLDialogElement) child.remove();
          else if (child.classList.contains("camo-chosen")) child.remove();
        }
        if (choice === "self") {
          pick.append(camoOpen.button, camoOpen.dialog);
        } else if (choice === "bot") {
          const held = botCamoFor(slot.name);
          pick.append(held.button, held.dialog);
        }
      };
      const options: [SeatChoice, string][] = [
        ["self", `You${session.handle ? ` (${at(session.handle)})` : ""}`],
        ["bot", "Play against AI"],
        ...lineup.map((handle): [SeatChoice, string] => [
          `p:${handle}`,
          at(handle),
        ]),
      ];
      const pick = el("div", { className: "seat-pick" }, [face, select]);
      const current = seats.get(slot.name) ?? "bot";
      for (const [value, label] of options) {
        select.append(
          el("option", {
            value,
            textContent: label,
            selected: value === current,
          }),
        );
      }
      select.addEventListener("change", () => {
        seats.set(slot.name, select.value as SeatChoice);
        setFace(select.value);
        setCamo(select.value);
        updateNext();
        // The seat's camo and caption both follow who holds it.
        refreshPreview();
      });
      const row = el("div", { className: "slot-row" }, [
        el("div", {}, [
          el("strong", { textContent: slot.name }),
          el("p", {
            className: "hint slot-units",
            textContent: slot.units.map((unit) => unit.name).join(", "),
          }),
        ]),
        pick,
      ]);
      setCamo(seats.get(slot.name) ?? "bot");
      // Drop target for a pilot tile dragged off Recent or Suggested — the
      // pointer-only complement to the select above, not a replacement for
      // it. See placeInSlot() for what a drop actually does.
      row.addEventListener("dragover", (event) => {
        if (!event.dataTransfer?.types.includes("text/plain")) return;
        event.preventDefault();
        row.classList.add("slot-row-drop-target");
      });
      row.addEventListener("dragleave", () => {
        row.classList.remove("slot-row-drop-target");
      });
      row.addEventListener("drop", (event) => {
        event.preventDefault();
        row.classList.remove("slot-row-drop-target");
        const handle = event.dataTransfer?.getData("text/plain");
        if (handle) placeInSlot(slot.name, handle);
      });
      slotList.append(row);
    }
    updateNext();
    refreshPreview();
  }

  /**
   * Whether the mapping can launch, said next to the button that will not.
   * The API enforces the same rules; this is the version that answers
   * before the round trip.
   */
  function updateNext(): void {
    if (!scenario) {
      next.disabled = true;
      return;
    }
    const chosen = scenario.slots.map((slot) => seats.get(slot.name) ?? "bot");
    const selves = chosen.filter((choice) => choice === "self").length;
    const players = chosen.filter((choice) => choice.startsWith("p:"));
    const doubled = players.find(
      (choice, index) => players.indexOf(choice) !== index,
    );
    if (selves !== 1) {
      seatPill.say("Take exactly one slot yourself.");
      next.disabled = true;
    } else if (doubled) {
      seatPill.say(`${at(doubled.slice(2))} can only hold one slot.`);
      next.disabled = true;
    } else {
      seatPill.say("");
      next.disabled = false;
    }
  }
  updateNext();

  // ----- launch ------------------------------------------------------------

  next.addEventListener("click", () => {
    const launched = scenario;
    if (!launched) return;
    next.disabled = true;
    next.textContent = "Deploying…";
    void (async () => {
      const plan: SeatPlan[] = [];
      for (const slot of launched.slots) {
        const choice = seats.get(slot.name) ?? "bot";
        if (choice === "self") {
          plan.push({ slot: slot.name, control: "self", camo: camo.rkey });
        } else if (choice === "bot") {
          // What the seat's own picker holds, not a fresh roll: the AI fights
          // in exactly the pattern the player was shown. Per seat, so a
          // scenario with three of them is not three copies of one camo.
          plan.push({
            slot: slot.name,
            control: "bot",
            botCamo:
              (await pickedBlob(botCamoFor(slot.name).picker)) ?? undefined,
          });
        } else {
          plan.push({
            slot: slot.name,
            control: "player",
            handle: choice.slice(2),
          });
        }
      }
      const matchId = await createMatch(launched.path, plan);
      render(...waitingScreen(session, matchId));
    })().catch((err: unknown) => {
      status.textContent =
        err instanceof ApiError
          ? err.message
          : "The match could not be started.";
      next.textContent = "Deploy";
      updateNext();
    });
  });

  const back = el("button", {
    type: "button",
    className: "secondary",
    textContent: "Back",
  });
  back.addEventListener("click", () => {
    render(...nav.matches!(session));
  });

  // No page head: "Challenge" was the label on the button that got the player
  // here, and the card under it already says what to do. The hidden h1 names
  // the page without repeating it on screen.
  return [
    el("h1", { className: "visually-hidden", textContent: "Challenge" }),
    el("section", { className: "card challenge" }, [
      el("h2", { textContent: "Set up the match" }),
      opponentsBlock,
      scenarioBlock,
      slotsBlock,
      el("div", { className: "row" }, [next, back]),
      status,
    ]),
    footer(),
  ];
}
